/**
 * The Details form shared by New and Edit.
 *
 * Both screens collect exactly the same fields, so they share one component
 * rather than drifting apart the way the product New/Edit forms did. Edit adds
 * a read-only phase badge: the lifecycle label is derived from the type and
 * window (`collectionPhase`), so there is nothing here to set.
 *
 * @see docs/spec/17-store-collections-design.md · S-CL2
 */
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, StatusPill } from "@flint/ui";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  TextField,
} from "@flint/ui/forms";
import { ModeCardRow, type ModeOption } from "../../../components/mode-card.tsx";
import { StoreFormSection, StoreFormRow } from "../products/store-form-section.tsx";
import { CollectionCoverField } from "./collection-cover-field.tsx";
import { TEXTAREA_CLASS } from "../products/product-form-constants.ts";
import { PHASE_LABEL, PHASE_TONE, phaseWithDates, fromDateInput } from "./collection-format.ts";

/**
 * The two kinds, in the order an operator meets them: Core is the catalog they
 * already have, Seasonal is the thing they're about to launch.
 */
const TYPE_OPTIONS: ReadonlyArray<ModeOption<"core" | "seasonal">> = [
  { id: "core", title: "Core", sub: "Always orderable. No dates." },
  {
    id: "seasonal",
    title: "Seasonal",
    sub: "Opens for pre-book, goes live, ends. Needs a season window.",
  },
];

export const collectionSchema = z
  .object({
    name: z.string().trim().min(1, "Name is required").max(120),
    type: z.enum(["core", "seasonal"]),
    activeFrom: z.string().trim(),
    activeTo: z.string().trim(),
    description: z.string().trim().max(2000),
    imageUrl: z.string().trim().max(2048),
  })
  .superRefine((v, ctx) => {
    // R1, caught here as well as in the domain so the operator sees it on the
    // field rather than as a toast after a round-trip.
    if (v.type !== "seasonal") return;
    if (!v.activeFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["activeFrom"],
        message: "Season window is required",
      });
    }
    if (!v.activeTo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["activeTo"],
        message: "Season window is required",
      });
    }
    if (v.activeFrom && v.activeTo && v.activeTo < v.activeFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["activeTo"],
        message: "End date is before start",
      });
    }
  });

export type CollectionFormValues = z.infer<typeof collectionSchema>;

export const EMPTY_COLLECTION: CollectionFormValues = {
  name: "",
  type: "core",
  activeFrom: "",
  activeTo: "",
  description: "",
  imageUrl: "",
};

/** Form values → the wire shape both `create` and `update` accept. */
export function toCollectionPayload(v: CollectionFormValues) {
  const seasonal = v.type === "seasonal";
  return {
    name: v.name.trim(),
    type: v.type,
    description: v.description.trim() ? v.description.trim() : null,
    imageUrl: v.imageUrl.trim() ? v.imageUrl.trim() : null,
    // Core never carries dates, even if the operator typed some before
    // switching type — the fields are hidden, so a stale value would be
    // invisible and still get saved.
    activeFrom: seasonal ? fromDateInput(v.activeFrom) : null,
    activeTo: seasonal ? fromDateInput(v.activeTo) : null,
  };
}

export interface CollectionFormProps {
  defaultValues: CollectionFormValues;
  /** Shown as a read-only badge on Edit; omitted on New, which has no phase yet. */
  phase?: keyof typeof PHASE_LABEL;
  /**
   * Locks the type picker. Spec 15 defines no type-change rule, so on Edit the
   * affordance is honestly absent rather than present-and-broken — changing
   * type means creating a new collection.
   */
  lockType?: boolean;
  busy: boolean;
  submitLabel: string;
  onSubmit: (values: CollectionFormValues) => void;
  onCancel: () => void;
  /** Rendered under Details — the product picker on New, nothing on Edit. */
  children?: React.ReactNode;
}

export function CollectionForm({
  defaultValues,
  phase,
  lockType = false,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
  children,
}: CollectionFormProps) {
  const form = useForm<CollectionFormValues>({
    resolver: zodResolver(collectionSchema),
    defaultValues,
  });

  const type = useWatch({ control: form.control, name: "type" });
  const isSeasonal = type === "seasonal";

  const phaseHint = phase
    ? phaseWithDates(phase, fromDateInput(defaultValues.activeFrom), fromDateInput(defaultValues.activeTo))
    : null;

  return (
    <Form {...form}>
      <form className="mt-5" onSubmit={(e) => e.preventDefault()}>
        {/* Sections carry their own card chrome (#265) — stack with a gap. */}
        <div className="flex flex-col gap-4">
          <StoreFormSection
            title="Details"
            help="Name the line sheet and pick how it sells. Core is your always-orderable catalog; a seasonal drop runs for a window you set."
          >
            <StoreFormRow single>
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <FormControl>
                      <ModeCardRow
                        value={field.value}
                        onChange={field.onChange}
                        options={TYPE_OPTIONS}
                        ariaLabel="Collection type"
                        className="sm:grid-cols-2 lg:grid-cols-2"
                        disabled={lockType}
                        lockedHint="Type is set at creation"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </StoreFormRow>

            <StoreFormRow>
              <TextField
                name="name"
                label="Name *"
                placeholder={isSeasonal ? "e.g. AW26 — Autumn/Winter 2026" : "e.g. Core"}
              />
            </StoreFormRow>

            {/* Hidden, not disabled, for Core: the dates don't apply rather than
                being temporarily unavailable. */}
            {isSeasonal ? (
              <StoreFormRow>
                <TextField name="activeFrom" label="Season start *" type="date" />
                <TextField name="activeTo" label="Season end *" type="date" />
              </StoreFormRow>
            ) : null}

            {phase ? (
              <StoreFormRow single>
                <div>
                  <div className="mb-1.5 text-sm font-medium leading-[18px] text-ink-2">Status</div>
                  <div className="flex items-center gap-2">
                    <StatusPill
                      tone={PHASE_TONE[phase]}
                      size="sm"
                      dot
                      {...(phaseHint ? { title: phaseHint } : {})}
                    >
                      {PHASE_LABEL[phase]}
                    </StatusPill>
                    <span className="text-xs text-muted">
                      {isSeasonal
                        ? "Derived from the season window — change the dates to move it."
                        : "Core is always live."}
                    </span>
                  </div>
                </div>
              </StoreFormRow>
            ) : null}

            <StoreFormRow single>
              <CollectionCoverField />
            </StoreFormRow>
            <StoreFormRow single>
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <textarea
                        {...field}
                        value={field.value ?? ""}
                        rows={3}
                        placeholder="Shown on line sheets and quote covers."
                        className={TEXTAREA_CLASS}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </StoreFormRow>
          </StoreFormSection>

          {children}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button intent="secondary" size="md" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            intent="primary"
            size="md"
            disabled={busy}
            onClick={() => form.handleSubmit(onSubmit)()}
          >
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
